import { DatabaseBlockModel } from '@blocksuite/affine-model';
import { uploadBlob } from '@blocksuite/affine-shared/utils';
import { assertDatabaseColumnShapeWithinLimits } from '@blocksuite/data-view';
import type { BlockStdScope } from '@blocksuite/std';
import type { Store } from '@blocksuite/store';
import { generateFractionalIndexingKeyBetween } from '@streamlain/database-identity';
import { PublicationError } from '@streamlain/doc-publication-contract';

import { createDatabaseBlockDataSource } from './data-source';
import { addProperty } from './utils/block-utils';

/**
 * Blob metadata for creating an attachment cell in a database row.
 */
export interface AttachmentBlobInfo {
  blobId: string;
  name: string;
  mime: string;
  /** Byte size from `File.size`. Zero is only valid for an empty file. */
  size: number;
}

/**
 * Creates attachment rows and any missing column in one local publication.
 * Resolves only after this operation's receipt and its scope have settled.
 */
export async function addRowsWithAttachments(
  store: Store,
  dbModel: DatabaseBlockModel,
  blobs: readonly AttachmentBlobInfo[]
): Promise<readonly string[]> {
  if (!blobs.length) return [];
  const dataSource = createDatabaseBlockDataSource(dbModel);
  assertCanAppendAttachmentRows(
    dbModel,
    blobs.length,
    'Database attachment import'
  );
  const metadata = dataSource.propertyMetaGet('attachment');
  if (!metadata) throw new PublicationError('FORMAT_MISMATCH');
  const rows = blobs.map(blob => ({
    rowId: store.workspace.idGenerator(),
    blob: { ...blob },
    order: generateFractionalIndexingKeyBetween(null, null),
  }));
  const propertyId = store.workspace.idGenerator();
  const scope = dataSource.openPublicationScope();
  try {
    const receipt = await dataSource.commitRowMutation({
      kind: 'create',
      changes: new Map(),
      rootMutation: stage => {
        const existing = stage.props.columns.some(
          column => column.type === 'attachment'
        );
        if (existing) return;
        if (stage.props.schemaLocked) throw new PublicationError('FORBIDDEN');
        try {
          assertDatabaseColumnShapeWithinLimits({
            source: 'Database attachment import',
            columnCount: stage.props.columns.length + (existing ? 0 : 1),
          });
        } catch {
          throw new PublicationError('LIMIT_EXCEEDED');
        }
        const names = new Set(stage.props.columns.map(column => column.name));
        let name = 'Attachments';
        for (let suffix = 2; names.has(name); suffix++)
          name = `Attachments ${suffix}`;
        addProperty(stage, 'end', { ...metadata.create(name), id: propertyId });
      },
      createRows: {
        position: 'end',
        actor: 'user',
        values: stage => {
          const column = stage.props.columns.find(
            column => column.type === 'attachment'
          );
          if (!column) throw new PublicationError('FORMAT_MISMATCH');
          return rows.map(({ rowId, blob, order }) => ({
            rowId,
            values: {
              title: blob.name,
              [column.id]: {
                [blob.blobId]: {
                  id: blob.blobId,
                  name: blob.name,
                  mime: blob.mime,
                  size: blob.size,
                  order,
                },
              },
            },
          }));
        },
      },
    });
    const receipts = await scope.settle();
    // The drop always writes rows: a write without receipt broke that.
    if (
      !receipt ||
      !receipts.some(member => member.operationId === receipt.operationId)
    )
      throw new PublicationError('FORMAT_MISMATCH');
    return rows.map(row => row.rowId);
  } finally {
    // A preparation/commit failure must close the scope before returning to DnD.
    await scope.settle();
  }
}

export function assertCanAppendAttachmentRows(
  dbModel: DatabaseBlockModel,
  rowCountToAppend: number,
  source: string
): void {
  if (!Number.isInteger(rowCountToAppend) || rowCountToAppend < 0) {
    throw new RangeError('Attachment row count must be a non-negative integer');
  }
  const dataSource = createDatabaseBlockDataSource(dbModel);

  const propertyIds = dataSource.getAllPropertyIds();
  const hasAttachmentColumn = propertyIds.some(
    propId => dataSource.propertyTypeGet(propId) === 'attachment'
  );

  assertDatabaseColumnShapeWithinLimits({
    source,
    columnCount: dataSource.columnCount$.value + (hasAttachmentColumn ? 0 : 1),
  });
}

/**
 * Uploads a File to the blob store and returns its metadata.
 * Accepts BlockStdScope to enable tus direct upload when available.
 */
export async function uploadFileAsBlob(
  std: BlockStdScope,
  file: File
): Promise<AttachmentBlobInfo> {
  const blobId = await uploadBlob(std, file);
  return {
    blobId,
    name: file.name,
    mime: file.type || 'application/octet-stream',
    size: file.size,
  };
}
